import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  ActionIcon,
  Button,
  Divider,
  Grid,
  List,
  Select,
  Skeleton,
  Textarea,
  TextInput
} from '@mantine/core';
import {
  AlertCircle,
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Edit,
  GripVertical,
  Plus,
  Trash
} from 'tabler-icons-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { Context as RegistrationAdminDashboardContext } from '../../../providers/RegistrationAdminDashboardProvider';
import { Context as RegistrationAdminContext } from '../../../providers/RegistrationAdminProvider';
import {
  useGlobalFormState,
  useMediaQueries,
  useModalState
} from '../../../helpers/hooks';
import AppStack from '../../common/AppStack';
import AppCard from '../../common/AppCard';
import AppFlexbox from '../../common/AppFlexbox';
import AppText from '../../common/AppText';
import ActionableIcon from '../../common/ActionableIcon';
import PaymentPlanAddInstallmentModal from './PaymentPlanAddInstallmentModal';
import SortableList from '../../common/SortableList';
import {
  getPaymentPlanInfo,
  getPaymentPlanInstallmentInfo
} from '../../../helpers/installmentsHelper';
import {
  PAYMENT_PLAN_TYPE_ENUM,
  PAYMENT_PLAN_TYPE_LIST,
  VIEW_ACTIONS_ENUM
} from '../../../config/constants';
import classes from '../../../styles/nestedStyles.module.css';
import { currencyFormat, singularPluralFormat } from '../../../helpers/format';
import { triggerNotification } from '../../../helpers/notificationHelper';
import ConfirmModal from '../../common/ConfirmModal';
import PaymentPlanAssignRegistrationFormsModal from './PaymentPlanAssignRegistrationFormsModal';

const INITIAL_FORM_STATE = {
  name: '',
  description: '',
  regAssociationPaymentPlanInstallments: [],
  divisionForms: [],
  active: false,
  expandedRegFormUuids: []
};

const PaymentPlanEditView = ({ duplicate }) => {
  const hasFetched = React.useRef(false);
  const navigate = useNavigate();
  const { pkRegAssociationPaymentPlan } = useParams();
  const {
    isTabletOrSmaller,
    isLaptopOrSmaller,
    isLargeMobileOrSmaller
  } = useMediaQueries();
  const {
    state,
    fetchAdminRegAssociationPaymentPlan,
    createAdminRegAssociationPaymentPlan,
    updateAdminRegAssociationPaymentPlans,
    deleteAdminRegAssociationPaymentPlans
  } = useContext(RegistrationAdminContext);
  const { state: dashboardState } = useContext(
    RegistrationAdminDashboardContext
  );
  const {
    state: modalState,
    onOpenModal,
    onCloseModal,
    onChangeModalLoading
  } = useModalState();
  const {
    hasInitialized,
    hasUnsavedChanges,
    formState,
    isSubmitting,
    setFormState,
    resetFormState,
    submitFormState,
    ConfirmDiscardModal
  } = useGlobalFormState(INITIAL_FORM_STATE, {
    confirmDiscard: true,
    containerWidth: 1100
  });
  const [
    fkRegAssociationPaymentPlanType,
    setFkRegAssociationPaymentPlanType
  ] = useState(PAYMENT_PLAN_TYPE_ENUM.CHECKOUT);
  const [apiValidationErrorState, setApiValidationErrorState] = useState({
    pkRegAssociationPaymentPlan: null,
    errors: []
  });
  const [showPaymentPlanErrors, setShowPaymentPlanErrors] = useState(false);
  const [expandProducts, setExpandProducts] = useState(false);

  const regAssociation = state.regAssociations.value.find(
    (a) =>
      a.pkRegAssociation ===
      dashboardState.regAssociation.value?.pkRegAssociation
  );

  const paymentPlanType = PAYMENT_PLAN_TYPE_LIST.find(
    (p) => p.value === fkRegAssociationPaymentPlanType
  );

  const regAssociationPaymentPlan =
    pkRegAssociationPaymentPlan &&
    pkRegAssociationPaymentPlan ===
      state.regAssociationPaymentPlan.value?.pkRegAssociationPaymentPlan.toString()
      ? state.regAssociationPaymentPlan.value
      : null;

  const apiValidationErrors =
    !duplicate &&
    pkRegAssociationPaymentPlan &&
    apiValidationErrorState.pkRegAssociationPaymentPlan?.toString() ===
      pkRegAssociationPaymentPlan
      ? apiValidationErrorState.errors
      : [];

  const loading =
    !regAssociation ||
    (!!pkRegAssociationPaymentPlan &&
      (!regAssociationPaymentPlan ||
        !hasInitialized ||
        state.regAssociationPaymentPlan.loading));

  const {
    isValidPaymentPlan,
    hasInstallmentErrors,
    totalFlatAmount,
    totalPercentage,
    installmentInfo,
    paymentPlanErrors,
    minimumEligibleCheckoutTotal,
    latestCheckoutDate
  } = getPaymentPlanInfo(formState);

  const installmentErrorMessages = installmentInfo
    .map((i) => i.error)
    .filter((e) => e);
  const hasErrors = installmentErrorMessages.length > 0;

  const registrationFormsGroupedByForm = formState.divisionForms.reduce(
    (r, c) => {
      const formId = c.regForm.uuid;

      const existing = r.find((group) => group.regForm.uuid === formId);
      if (!existing) {
        r.push({
          regForm: c.regForm,
          divisionForms: [c]
        });
      }
      else {
        existing.divisionForms.push(c);
      }
      return r;
    },
    []
  );

  useEffect(() => {
    if (
      regAssociation &&
      pkRegAssociationPaymentPlan &&
      regAssociationPaymentPlan?.pkRegAssociationPaymentPlan.toString() !==
        pkRegAssociationPaymentPlan
    ) {
      fetchAdminRegAssociationPaymentPlan(
        regAssociation.pkRegAssociation,
        pkRegAssociationPaymentPlan,
        () => {},
        (error) => triggerNotification(error)
      );
      hasFetched.current = true;
    }
  }, [regAssociation, pkRegAssociationPaymentPlan]);

  useEffect(() => {
    if (
      !state.regAssociationPaymentPlan.loading &&
      (!pkRegAssociationPaymentPlan || regAssociationPaymentPlan)
    ) {
      if (regAssociationPaymentPlan) {
        setFkRegAssociationPaymentPlanType(
          regAssociationPaymentPlan.fkRegAssociationPaymentPlanType.toString()
        );
      }

      resetFormState({
        name: regAssociationPaymentPlan?.name ?? '',
        description: regAssociationPaymentPlan?.description ?? '',
        active: regAssociationPaymentPlan?.active ?? false,
        divisionForms:
          regAssociationPaymentPlan?.regAssociationDivisionFormPaymentPlans?.map(
            (p) => ({ ...p.regAssociationDivisionForm })
          ) ?? [],
        regAssociationPaymentPlanInstallments:
          regAssociationPaymentPlan?.regAssociationPaymentPlanInstallments.map(
            (i) => ({
              ...i,
              key: i.pkRegAssociationPaymentPlanInstallment,
              id: i.pkRegAssociationPaymentPlanInstallment
            })
          ) ?? [],
        expandedRegFormUuids: []
      });
    }
  }, [
    regAssociationPaymentPlan,
    state.regAssociationPaymentPlan.loading,
    duplicate
  ]);

  useEffect(() => {
    if (paymentPlanErrors.length === 0 && showPaymentPlanErrors) {
      setShowPaymentPlanErrors(false);
    }
  }, [paymentPlanErrors, showPaymentPlanErrors]);

  const triggerSaveNotification = (errors, successMessage) => {
    if (errors.length > 0) {
      triggerNotification(
        'The payment plan was saved, but it could not be activated.',
        'warning'
      );
    }
    else {
      triggerNotification(successMessage, 'success');
    }
  };

  const onSavePaymentPlan = () => {
    if (isValidPaymentPlan) {
      setShowPaymentPlanErrors(false);

      submitFormState((formData, onErrorCallback) => {
        if (regAssociationPaymentPlan && !duplicate) {
          updateAdminRegAssociationPaymentPlans(
            regAssociation.pkRegAssociation,
            {
              selectFirstItem: true,
              regAssociationPaymentPlans: [
                {
                  ...formState,
                  pkRegAssociationPaymentPlan:
                    regAssociationPaymentPlan.pkRegAssociationPaymentPlan,
                  pkRegAssociationDivisionForms: formState.divisionForms.map(
                    (df) => df.pkRegAssociationDivisionForm
                  )
                }
              ]
            },
            (data) => {
              const updatedPlan = data?.find(
                (p) =>
                  p.pkRegAssociationPaymentPlan ===
                  regAssociationPaymentPlan.pkRegAssociationPaymentPlan
              );
              const errors = updatedPlan?.errors ?? [];
              setApiValidationErrorState({
                pkRegAssociationPaymentPlan:
                  regAssociationPaymentPlan.pkRegAssociationPaymentPlan,
                errors
              });
              triggerSaveNotification(errors, 'Payment plan updated!');
            },
            (error) => {
              triggerNotification(error);
              setFormState({ ...formState, loading: false });
            }
          );
        }
        else {
          createAdminRegAssociationPaymentPlan(
            regAssociation.pkRegAssociation,
            {
              ...formState,
              fkRegAssociationPaymentPlanType,
              pkRegAssociationDivisionForms: formState.divisionForms.map(
                (df) => df.pkRegAssociationDivisionForm
              )
            },
            (data) => {
              const errors = data?.errors ?? [];
              navigate(
                `/admin/settings/${regAssociation.pkRegAssociation}/payment-plans/${data.pkRegAssociationPaymentPlan}`
              );
              setApiValidationErrorState({
                pkRegAssociationPaymentPlan: data.pkRegAssociationPaymentPlan,
                errors
              });
              triggerSaveNotification(errors, 'Payment plan created!');
            },
            onErrorCallback
          );
        }
      });
    }
    else {
      setShowPaymentPlanErrors(true);
    }
  };

  return !loading ? (
    <AppStack
      style={{
        flex: 1,
        overflow: 'auto'
      }}
    >
      <AppStack
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();

          onSavePaymentPlan();
        }}
        p={{ base: '8px 0px', sm: '16px 16px 80px 16px' }}
        style={{
          flex: 1,
          margin: 'auto',
          width: '100%',
          maxWidth: 1100,
          paddingTop: 16,
          gap: 16
        }}
      >
        <AppFlexbox
          p={{ base: '0px 8px', sm: 0 }}
          style={{
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%'
          }}
        >
          <AppFlexbox style={{ gap: 5, alignItems: 'center' }}>
            <ActionableIcon
              color="dark"
              component={Link}
              radius="md"
              to={`/admin/settings/${regAssociation?.pkRegAssociation}/payment-plans`}
              variant="subtle"
            >
              <ArrowLeft />
            </ActionableIcon>
            <AppStack style={{ gap: 0 }}>
              <AppText style={{ fontSize: 18, fontWeight: 700 }}>
                {regAssociationPaymentPlan?.name ??
                  (paymentPlanType
                    ? `Create ${paymentPlanType.label} Plan`
                    : 'Select Plan Type')}
              </AppText>

              {regAssociationPaymentPlan && !duplicate && (
                <AppText style={{ fontSize: 12, color: '#666' }}>
                  Created{' '}
                  {dayjs(regAssociationPaymentPlan.createdAt).format(
                    'MMM D, YYYY'
                  )}
                </AppText>
              )}
            </AppStack>
          </AppFlexbox>

          {!!regAssociationPaymentPlan && !duplicate && (
            <AppFlexbox style={{ gap: 5, alignItems: 'center' }}>
              <Button
                color="dark"
                component={Link}
                disabled={isSubmitting}
                radius="md"
                size="xs"
                to={`/admin/settings/${regAssociation?.pkRegAssociation}/payment-plans/${regAssociationPaymentPlan.pkRegAssociationPaymentPlan}/duplicate`}
                type="button"
                variant="light"
              >
                Duplicate
              </Button>
            </AppFlexbox>
          )}
        </AppFlexbox>

        {apiValidationErrors.length > 0 && (
          <AppCard
            radius={isTabletOrSmaller ? 0 : 'md'}
            shadow="xs"
            style={{ padding: 0 }}
            withBorder
          >
            <AppFlexbox
              style={{
                padding: 8,
                gap: 8,
                backgroundColor: '#c40000',
                color: '#fff'
              }}
            >
              <AppStack>
                <AlertCircle size={18} />
              </AppStack>
              <AppText style={{ fontSize: 14 }}>
                This payment plan was saved but could not be activated.
              </AppText>
            </AppFlexbox>
            <AppStack style={{ padding: 8, gap: 0 }}>
              <List withPadding>
                {apiValidationErrors.map((error, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <List.Item key={index}>
                    <AppText style={{ fontSize: 14 }}>{error}</AppText>
                  </List.Item>
                ))}
              </List>
            </AppStack>
          </AppCard>
        )}

        {hasErrors ||
        (showPaymentPlanErrors && paymentPlanErrors.length > 0) ? (
          <AppCard
            radius={isTabletOrSmaller ? 0 : 'md'}
            shadow="xs"
            style={{ padding: 0 }}
            withBorder
          >
            <AppFlexbox
              style={{
                padding: 8,
                gap: 8,
                backgroundColor: '#c40000',
                color: '#fff'
              }}
            >
              <AppStack>
                <AlertCircle size={18} />
              </AppStack>
              <AppText style={{ fontSize: 14 }}>
                Please fix the errors before saving.
              </AppText>
            </AppFlexbox>
            <AppStack style={{ padding: 8, gap: 0 }}>
              <List withPadding>
                {installmentErrorMessages.map((error, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <List.Item key={index}>
                    <AppText style={{ fontSize: 14 }}>{error}</AppText>
                  </List.Item>
                ))}
                {showPaymentPlanErrors &&
                  paymentPlanErrors.length > 0 &&
                  paymentPlanErrors.map((error) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <List.Item key={error.key}>
                      <AppText style={{ fontSize: 14 }}>
                        {error.message}
                      </AppText>
                    </List.Item>
                  ))}
              </List>
            </AppStack>
          </AppCard>
        ) : duplicate ? (
          <AppCard
            radius={isTabletOrSmaller ? 0 : 'md'}
            shadow="xs"
            style={{ padding: 8 }}
            withBorder
          >
            <AppFlexbox style={{ gap: 8 }}>
              <AppStack>
                <AlertCircle color="#eca70a" style={{ size: 18 }} />
              </AppStack>
              <AppText style={{ fontSize: 14 }}>
                Duplicate this payment plan by changing the <b>Title</b> and
                clicking save.
              </AppText>
            </AppFlexbox>
          </AppCard>
        ) : (
          <AppCard
            radius={isTabletOrSmaller ? 0 : 'md'}
            shadow="xs"
            style={{ padding: 8 }}
            withBorder
          >
            <AppFlexbox style={{ gap: 8 }}>
              <AppStack>
                <AlertCircle color="dodgerblue" style={{ size: 18 }} />
              </AppStack>
              <AppText style={{ fontSize: 14 }}>
                Use payment plans to setup installment payments for your
                checkout.
              </AppText>
            </AppFlexbox>
          </AppCard>
        )}

        {!paymentPlanType ? (
          <AppStack
            style={{
              flex: 1,
              alignItems: 'center',
              gap: 16
            }}
          >
            <AppText
              style={{
                fontSize: 14,
                color: '#666',
                textAlign: 'center',
                fontStyle: 'italic'
              }}
            >
              Select the type of payment plan you would like to create.
            </AppText>
            <AppStack style={{ width: '100%', maxWidth: 900, gap: 16 }}>
              <Grid columns={isTabletOrSmaller ? 1 : 2}>
                {PAYMENT_PLAN_TYPE_LIST.map((option) => (
                  <Grid.Col key={option.value} span={1}>
                    <AppCard
                      className={classes['hover-action-card-outlined']}
                      onClick={(e) =>
                        setFkRegAssociationPaymentPlanType(option.value)
                      }
                      radius="md"
                      shadow="lg"
                      style={{ padding: 0, flex: 1, height: '100%' }}
                      withBorder
                    >
                      <AppStack style={{ gap: 0 }}>
                        <AppFlexbox
                          style={{
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: 12
                          }}
                        >
                          <AppStack>
                            <AppText style={{ fontSize: 18, fontWeight: 500 }}>
                              {option.label}
                            </AppText>
                          </AppStack>
                        </AppFlexbox>
                        <Divider />

                        <AppStack
                          style={{
                            gap: 8,
                            padding: 12,
                            justifyContent: 'space-between'
                          }}
                        >
                          <AppStack style={{}}>
                            <AppText style={{ fontSize: 14, color: '#666' }}>
                              {option.description}
                            </AppText>
                          </AppStack>
                          <AppText
                            style={{
                              fontSize: 14,
                              color: '#666',
                              fontStyle: 'italic',
                              paddingTop: 8
                            }}
                          >
                            {option.disclaimer}
                          </AppText>
                        </AppStack>
                      </AppStack>
                    </AppCard>
                  </Grid.Col>
                ))}
              </Grid>
            </AppStack>
          </AppStack>
        ) : (
          <AppFlexbox
            style={{ flexDirection: isLaptopOrSmaller ? 'column' : 'row' }}
          >
            <AppStack
              style={{
                flex: 2,
                gap: 16
              }}
            >
              <AppCard
                radius={isTabletOrSmaller ? 0 : 'md'}
                shadow="xs"
                style={{ padding: 16 }}
                withBorder
              >
                <AppStack>
                  <TextInput
                    disabled={isSubmitting}
                    label="Title"
                    onChange={(e) =>
                      setFormState({
                        ...formState,
                        name: e.currentTarget.value
                      })
                    }
                    required
                    value={formState.name}
                  />

                  <Textarea
                    autosize
                    disabled={isSubmitting}
                    label="Description"
                    minRows={4}
                    onChange={(e) =>
                      setFormState({
                        ...formState,
                        description: e.currentTarget.value
                      })
                    }
                    value={formState.description}
                  />
                </AppStack>
              </AppCard>

              <AppCard
                radius={isTabletOrSmaller ? 0 : 'md'}
                shadow="xs"
                style={{ padding: 0 }}
                withBorder
              >
                <AppStack style={{ gap: 0 }}>
                  <AppFlexbox
                    style={{
                      padding: 16,
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <AppText style={{ fontSize: 16, fontWeight: 700 }}>
                      Installments
                    </AppText>

                    <AppFlexbox style={{ gap: 8 }}>
                      <Button
                        color="dark"
                        disabled={isSubmitting}
                        leftSection={<Plus size={18} />}
                        onClick={() => onOpenModal('INSTALLMENT')}
                        size="compact-sm"
                        type="button"
                        variant="filled"
                      >
                        Add
                      </Button>
                    </AppFlexbox>
                  </AppFlexbox>

                  {formState.regAssociationPaymentPlanInstallments.length ===
                  0 ? (
                    <AppStack
                      style={{ padding: 16, gap: 8, alignItems: 'center' }}
                    >
                      <AppText
                        style={{
                          fontSize: 14,
                          color: '#666',
                          textAlign: 'center',
                          fontStyle: 'italic'
                        }}
                      >
                        No installments have been added yet.
                      </AppText>
                      <Button
                        color="dark"
                        disabled={isSubmitting}
                        leftSection={<Plus size={18} />}
                        onClick={() => onOpenModal('INSTALLMENT')}
                        size="compact-sm"
                        type="button"
                        variant="filled"
                      >
                        Add Installment
                      </Button>
                    </AppStack>
                  ) : (
                    <>
                      <SortableList
                        items={
                          expandProducts || !regAssociationPaymentPlan
                            ? formState.regAssociationPaymentPlanInstallments
                            : [
                                ...formState.regAssociationPaymentPlanInstallments
                              ]
                                .sort((a, b) => a.sort - b.sort)
                                .slice(0, 5)
                        }
                        onChange={(newList) => {
                          setFormState({
                            ...formState,
                            regAssociationPaymentPlanInstallments: [
                              ...formState.regAssociationPaymentPlanInstallments.filter(
                                (p) => !newList.find((n) => n.key === p.key)
                              ),
                              ...newList.map((p, index) => ({
                                ...p,
                                sort: index + 1
                              }))
                            ]
                          });
                        }}
                        renderItem={(
                          item,
                          {
                            attributes,
                            listeners,
                            isDragging,
                            setNodeRef,
                            style,
                            isOverlay
                          }
                        ) => {
                          const {
                            error: installmentErrorMessage,
                            paymentDateInfo,
                            paymentAmountInfo
                          } = getPaymentPlanInstallmentInfo(
                            item,
                            formState.regAssociationPaymentPlanInstallments
                          );

                          return (
                            <AppCard
                              ref={setNodeRef}
                              radius={0}
                              shadow="none"
                              style={{
                                ...style,
                                padding: 0,
                                outline: isOverlay
                                  ? 'solid 1px #dee2e6'
                                  : 'none',
                                backgroundColor: isDragging
                                  ? 'rgba(56, 56, 56, 0.1)'
                                  : '#FFF',
                                opacity: isDragging ? 0.4 : 1,
                                cursor: isDragging ? 'grabbing' : 'default'
                              }}
                            >
                              <AppFlexbox
                                style={{
                                  padding: 10,
                                  gap: 10,
                                  borderTop: isOverlay
                                    ? 'none'
                                    : 'solid 1px #dee2e6',
                                  flex: 1,
                                  alignItems: 'center'
                                }}
                              >
                                <ActionIcon
                                  color="dark"
                                  disabled={isSubmitting}
                                  variant="subtle"
                                  {...attributes}
                                  {...listeners}
                                >
                                  <GripVertical size={20} />
                                </ActionIcon>

                                <AppFlexbox
                                  style={{
                                    flex: 1,
                                    gap: 10,
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                  }}
                                >
                                  <AppFlexbox style={{ alignItems: 'center' }}>
                                    <AppStack style={{ gap: 0 }}>
                                      <AppText
                                        style={{
                                          fontSize: 16,
                                          fontWeight: 500
                                        }}
                                      >
                                        {paymentAmountInfo}
                                      </AppText>
                                      <AppText
                                        style={{
                                          fontSize: 14,
                                          color: '#666'
                                        }}
                                      >
                                        {paymentDateInfo}
                                      </AppText>

                                      {installmentErrorMessage && (
                                        <AppFlexbox
                                          style={{
                                            gap: 5,
                                            alignItems: 'center'
                                          }}
                                        >
                                          <AlertCircle
                                            color="#C40000"
                                            size={16}
                                          />
                                          <AppText
                                            style={{
                                              fontSize: 14,
                                              color: '#C40000'
                                            }}
                                          >
                                            {installmentErrorMessage}
                                          </AppText>
                                        </AppFlexbox>
                                      )}
                                    </AppStack>
                                  </AppFlexbox>

                                  <AppFlexbox>
                                    <ActionIcon
                                      color="dark"
                                      disabled={isSubmitting}
                                      onClick={() => {
                                        onOpenModal('INSTALLMENT', item);
                                      }}
                                      radius="md"
                                      size={20}
                                      variant="subtle"
                                    >
                                      <Edit size={20} />
                                    </ActionIcon>
                                    <ActionIcon
                                      color="red"
                                      disabled={isSubmitting}
                                      onClick={() => {
                                        setFormState({
                                          ...formState,
                                          regAssociationPaymentPlanInstallments: formState.regAssociationPaymentPlanInstallments.filter(
                                            (p) => p.key !== item.key
                                          )
                                        });
                                      }}
                                      radius="md"
                                      size={20}
                                      variant="subtle"
                                    >
                                      <Trash size={20} />
                                    </ActionIcon>
                                  </AppFlexbox>
                                </AppFlexbox>
                              </AppFlexbox>
                            </AppCard>
                          );
                        }}
                      />
                      {regAssociationPaymentPlan &&
                        formState.regAssociationPaymentPlanInstallments.length >
                          5 && (
                          <>
                            <Divider />

                            <AppFlexbox
                              onClick={() => setExpandProducts(!expandProducts)}
                              style={{
                                flex: 1,
                                padding: 10,
                                gap: 5,
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#f8f9fa',
                                cursor: 'pointer'
                              }}
                            >
                              {expandProducts ? (
                                <>
                                  <AppText
                                    style={{ fontSize: 14, fontWeight: 500 }}
                                  >
                                    Show less
                                  </AppText>

                                  <ChevronUp size={16} />
                                </>
                              ) : (
                                <>
                                  <AppText
                                    style={{ fontSize: 14, fontWeight: 500 }}
                                  >
                                    Show all{' '}
                                    {
                                      formState
                                        .regAssociationPaymentPlanInstallments
                                        .length
                                    }{' '}
                                    installments
                                  </AppText>

                                  <ChevronDown size={16} />
                                </>
                              )}
                            </AppFlexbox>
                          </>
                        )}
                    </>
                  )}
                </AppStack>
              </AppCard>
            </AppStack>

            <AppStack style={{ flex: 1 }}>
              <AppCard
                radius={isTabletOrSmaller ? 0 : 'md'}
                shadow="xs"
                style={{ padding: 0 }}
                withBorder
              >
                <AppStack style={{ padding: 16, gap: 16 }}>
                  <AppFlexbox
                    style={{
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <AppText style={{ fontSize: 16, fontWeight: 700 }}>
                      Status
                    </AppText>
                  </AppFlexbox>

                  <AppStack style={{ gap: 0 }}>
                    <AppFlexbox style={{ gap: 8 }}>
                      <Select
                        data={[
                          {
                            label: 'Active',
                            value: 'active'
                          },
                          {
                            label: 'Disabled',
                            value: 'disabled'
                          }
                        ]}
                        onChange={(v) => {
                          if (v) {
                            setFormState({
                              ...formState,
                              active: v === 'active'
                            });
                          }
                        }}
                        style={{ flex: 1 }}
                        value={formState.active ? 'active' : 'disabled'}
                      />
                    </AppFlexbox>
                  </AppStack>
                </AppStack>
              </AppCard>

              {paymentPlanType.value ===
                PAYMENT_PLAN_TYPE_ENUM.REGISTRATION_FORM && (
                <AppCard
                  radius={isTabletOrSmaller ? 0 : 'md'}
                  shadow="xs"
                  style={{ padding: 0 }}
                  withBorder
                >
                  <AppStack style={{ gap: 0 }}>
                    <AppFlexbox
                      style={{
                        padding: 16,
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <AppText style={{ fontSize: 16, fontWeight: 700 }}>
                        Registrations
                      </AppText>

                      <AppFlexbox style={{ gap: 8 }}>
                        <Button
                          color="dark"
                          disabled={isSubmitting}
                          leftSection={<Edit size={18} />}
                          onClick={() => onOpenModal('REGISTRATION_FORM')}
                          size="compact-sm"
                          type="button"
                          variant="filled"
                        >
                          Assign
                        </Button>
                      </AppFlexbox>
                    </AppFlexbox>

                    <AppStack style={{ gap: 0, padding: 16, paddingTop: 0 }}>
                      {formState.divisionForms.length === 0 ? (
                        <AppStack style={{ gap: 8, alignItems: 'center' }}>
                          <AppText
                            style={{
                              fontSize: 14,
                              color: '#666',
                              textAlign: 'center',
                              fontStyle: 'italic'
                            }}
                          >
                            No registrations have been added yet.
                          </AppText>
                        </AppStack>
                      ) : (
                        <AppStack style={{ gap: 8 }}>
                          <AppText
                            style={{
                              fontSize: 14,
                              color: '#666',
                              textAlign: 'center',
                              fontStyle: 'italic'
                            }}
                          >
                            {formState.divisionForms.length} registrations
                            assigned
                          </AppText>

                          <AppStack>
                            {registrationFormsGroupedByForm
                              .slice(0, 5)
                              .map((d) => {
                                const isExpanded = formState.expandedRegFormUuids.includes(
                                  d.regForm.uuid
                                );

                                return (
                                  <AppCard
                                    key={d.regForm.uuid}
                                    className={classes['hover-action-card']}
                                    onClick={() => {
                                      setFormState({
                                        ...formState,
                                        expandedRegFormUuids: formState.expandedRegFormUuids.includes(
                                          d.regForm.uuid
                                        )
                                          ? formState.expandedRegFormUuids.filter(
                                              (uuid) => uuid !== d.regForm.uuid
                                            )
                                          : [
                                              ...formState.expandedRegFormUuids,
                                              d.regForm.uuid
                                            ]
                                      });
                                    }}
                                    radius="md"
                                    shadow="xs"
                                    style={{ padding: 8 }}
                                    withBorder
                                  >
                                    <AppStack
                                      key={d.regForm.uuid}
                                      style={{ gap: 8 }}
                                    >
                                      <AppFlexbox
                                        style={{
                                          gap: 8,
                                          alignItems: 'center',
                                          justifyContent: 'space-between'
                                        }}
                                      >
                                        <AppStack style={{ gap: 0 }}>
                                          <AppText
                                            style={{
                                              fontSize: 14,
                                              fontWeight: 500
                                            }}
                                          >
                                            {d.regForm.name}
                                          </AppText>
                                          <AppText
                                            style={{
                                              fontSize: 14,
                                              color: '#666'
                                            }}
                                          >
                                            {singularPluralFormat(
                                              d.divisionForms.length,
                                              'division',
                                              'divisions'
                                            )}
                                          </AppText>
                                        </AppStack>

                                        <AppStack>
                                          {isExpanded ? (
                                            <ChevronUp size={16} />
                                          ) : (
                                            <ChevronDown size={16} />
                                          )}
                                        </AppStack>
                                      </AppFlexbox>

                                      {isExpanded && (
                                        <>
                                          <Divider />
                                          <AppStack
                                            style={{ padding: 8, gap: 8 }}
                                          >
                                            {d.divisionForms.map((df) => (
                                              <AppStack style={{ gap: 0 }}>
                                                <AppText
                                                  style={{
                                                    fontSize: 14,
                                                    fontWeight: 500
                                                  }}
                                                >
                                                  {
                                                    df.regAssociationDivision
                                                      .name
                                                  }
                                                </AppText>
                                                <AppText
                                                  style={{
                                                    fontSize: 14,
                                                    color: '#666'
                                                  }}
                                                >
                                                  {
                                                    df.regAssociationDivision
                                                      .regAssociationSeason.name
                                                  }
                                                </AppText>
                                              </AppStack>
                                            ))}
                                          </AppStack>
                                        </>
                                      )}
                                    </AppStack>
                                  </AppCard>
                                );
                              })}

                            {registrationFormsGroupedByForm.length > 5 && (
                              <AppStack>
                                <Divider
                                  label="View all"
                                  onClick={() =>
                                    onOpenModal('REGISTRATION_FORM')
                                  }
                                  style={{ cursor: 'pointer' }}
                                />
                              </AppStack>
                            )}
                          </AppStack>
                        </AppStack>
                      )}
                    </AppStack>
                  </AppStack>
                </AppCard>
              )}

              <AppCard
                radius={isTabletOrSmaller ? 0 : 'md'}
                shadow="xs"
                style={{ padding: 0 }}
                withBorder
              >
                <AppStack style={{ padding: 16, gap: 16 }}>
                  <AppFlexbox
                    style={{
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <AppText style={{ fontSize: 16, fontWeight: 700 }}>
                      Summary
                    </AppText>
                  </AppFlexbox>

                  {formState.regAssociationPaymentPlanInstallments.length ===
                  0 ? (
                    <AppStack
                      style={{ gap: 8, alignItems: 'center', padding: 16 }}
                    >
                      <AppText
                        style={{
                          fontSize: 14,
                          color: '#666',
                          textAlign: 'center',
                          fontStyle: 'italic'
                        }}
                      >
                        No installments added.
                      </AppText>
                    </AppStack>
                  ) : (
                    <AppStack style={{ gap: 8 }}>
                      <AppFlexbox
                        style={{
                          gap: 8,
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}
                      >
                        <AppText style={{ fontSize: 14 }}>
                          # of Installments:
                        </AppText>

                        <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                          {
                            formState.regAssociationPaymentPlanInstallments
                              .length
                          }
                        </AppText>
                      </AppFlexbox>

                      {totalPercentage > 0 && (
                        <AppFlexbox
                          style={{
                            gap: 8,
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}
                        >
                          <AppText style={{ fontSize: 14 }}>
                            Percent Allocated:
                          </AppText>

                          <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                            {totalPercentage / 100}%
                          </AppText>
                        </AppFlexbox>
                      )}

                      {totalFlatAmount > 0 && (
                        <AppFlexbox
                          style={{
                            gap: 8,
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}
                        >
                          <AppText style={{ fontSize: 14 }}>
                            Fixed Amounts:
                          </AppText>

                          <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                            {currencyFormat(totalFlatAmount / 100)}
                          </AppText>
                        </AppFlexbox>
                      )}

                      {minimumEligibleCheckoutTotal > 0 && (
                        <AppFlexbox
                          style={{
                            gap: 8,
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}
                        >
                          <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                            Minimum Eligible Total:
                          </AppText>

                          <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                            {currencyFormat(minimumEligibleCheckoutTotal / 100)}
                          </AppText>
                        </AppFlexbox>
                      )}

                      {latestCheckoutDate && (
                        <AppFlexbox
                          style={{
                            gap: 8,
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}
                        >
                          <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                            Latest Checkout Date:
                          </AppText>

                          <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                            {dayjs(latestCheckoutDate).format('MMM D, YYYY')}
                          </AppText>
                        </AppFlexbox>
                      )}
                    </AppStack>
                  )}

                  <Divider />

                  <AppStack style={{ gap: 0 }}>
                    <Button
                      color="dark"
                      disabled={
                        !hasUnsavedChanges ||
                        isSubmitting ||
                        hasInstallmentErrors ||
                        installmentInfo.length === 0 ||
                        (showPaymentPlanErrors && paymentPlanErrors.length > 0)
                      }
                      id="save-button"
                      radius="md"
                      size="sm"
                      type="submit"
                      variant="filled"
                    >
                      {regAssociationPaymentPlan && !duplicate
                        ? 'Save changes'
                        : 'Create plan'}
                    </Button>
                  </AppStack>
                </AppStack>
              </AppCard>
            </AppStack>
          </AppFlexbox>
        )}

        <AppFlexbox
          p={{ base: 16, sm: '16px 0px' }}
          style={{
            justifyContent: 'flex-end',
            flexDirection: isLargeMobileOrSmaller ? 'column-reverse' : 'row'
          }}
        >
          {!!regAssociationPaymentPlan && !duplicate && (
            <Button
              color="red"
              disabled={isSubmitting}
              onClick={() => {
                onOpenModal(
                  VIEW_ACTIONS_ENUM.DELETE,
                  regAssociationPaymentPlan
                );
              }}
              radius="md"
              size={isTabletOrSmaller ? 'sm' : 'compact-sm'}
              type="button"
              variant="filled"
            >
              Delete plan
            </Button>
          )}
          <Button
            color="dark"
            disabled={!hasUnsavedChanges}
            id="save-button"
            loading={isSubmitting}
            radius="md"
            size={isTabletOrSmaller ? 'sm' : 'compact-sm'}
            type="submit"
            variant="filled"
          >
            Save
          </Button>
        </AppFlexbox>

        <PaymentPlanAddInstallmentModal
          installment={
            modalState.action === 'INSTALLMENT' ? modalState.item : null
          }
          installments={formState.regAssociationPaymentPlanInstallments}
          isOpen={modalState.isOpen && modalState.action === 'INSTALLMENT'}
          onClose={onCloseModal}
          onConfirm={(installment) => {
            setFormState({
              ...formState,
              regAssociationPaymentPlanInstallments: modalState.item
                ? [
                    ...formState.regAssociationPaymentPlanInstallments.map(
                      (i) =>
                        i.key === modalState.item.key
                          ? {
                              ...i,
                              ...installment
                            }
                          : i
                    )
                  ]
                : [
                    ...formState.regAssociationPaymentPlanInstallments,
                    {
                      ...installment,
                      key: `installment-${Date.now()}`,
                      sort:
                        Math.max(
                          0,
                          ...formState.regAssociationPaymentPlanInstallments.map(
                            (i) => i.sort
                          )
                        ) + 1
                    }
                  ]
            });
            setExpandProducts(true);
            onCloseModal();
          }}
        />

        <PaymentPlanAssignRegistrationFormsModal
          isOpen={
            modalState.isOpen && modalState.action === 'REGISTRATION_FORM'
          }
          onClose={onCloseModal}
          onConfirm={(divisionForms) => {
            setFormState({
              ...formState,
              divisionForms
            });
            onCloseModal();
          }}
          pkRegAssociation={regAssociation.pkRegAssociation}
          regAssociationDivisionForms={formState.divisionForms}
        />

        <ConfirmModal
          confirmActionColor="red"
          confirmActionText="Yes, delete plan"
          isLoading={modalState.loading}
          isNewModalStyle
          isOpen={
            modalState.isOpen && modalState.action === VIEW_ACTIONS_ENUM.DELETE
          }
          message={
            <AppStack style={{ gap: 10 }}>
              <AppText>
                Are you sure you want to delete <b>{modalState.item?.name}</b>?
              </AppText>

              <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                This action can’t be undone.
              </AppText>
            </AppStack>
          }
          onCancel={onCloseModal}
          onConfirm={() => {
            onChangeModalLoading(true);
            deleteAdminRegAssociationPaymentPlans(
              regAssociation.pkRegAssociation,
              [pkRegAssociationPaymentPlan],
              () => {
                triggerNotification('Plan deleted!', 'success');
                navigate(
                  `/admin/settings/${regAssociation.pkEcomStore}/payment-plans`
                );
              },
              (error) => {
                triggerNotification(error);
                onChangeModalLoading(false);
              }
            );
          }}
          title={`Delete ${modalState.item?.name}?`}
          topPadding={0}
        />

        {ConfirmDiscardModal}
      </AppStack>
    </AppStack>
  ) : (
    <AppStack
      p={{ base: '8px 0px', sm: '16px 16px 80px 16px' }}
      style={{
        flex: 1,
        margin: 'auto',
        width: '100%',
        maxWidth: 1100,
        paddingTop: 16,
        gap: 16
      }}
    >
      <AppFlexbox
        p={{ base: '0px 8px', sm: 0 }}
        style={{
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%'
        }}
      >
        <AppFlexbox style={{ gap: 5 }}>
          <Skeleton height={30} width={200} />
        </AppFlexbox>
      </AppFlexbox>

      <AppCard
        radius={isTabletOrSmaller ? 0 : 'md'}
        shadow="xs"
        style={{ padding: 16 }}
        withBorder
      >
        <AppStack style={{ gap: 10 }}>
          <Skeleton height={10} width="100%" />
        </AppStack>
      </AppCard>

      {pkRegAssociationPaymentPlan ? (
        <AppFlexbox
          style={{ flexDirection: isLaptopOrSmaller ? 'column' : 'row' }}
        >
          <AppStack style={{ gap: 16, flex: 2 }}>
            <AppCard
              radius={isTabletOrSmaller ? 0 : 'md'}
              shadow="xs"
              style={{ padding: 16 }}
              withBorder
            >
              <AppStack style={{ gap: 10 }}>
                <AppStack style={{ gap: 5 }}>
                  <Skeleton height={10} width="30%" />
                  <Skeleton height={32} width="100%" />
                </AppStack>

                <AppStack style={{ gap: 5 }}>
                  <Skeleton height={10} width="30%" />
                  <Skeleton height={100} width="100%" />
                </AppStack>
              </AppStack>
            </AppCard>
            <AppCard
              radius={isTabletOrSmaller ? 0 : 'md'}
              shadow="xs"
              style={{ padding: 16 }}
              withBorder
            >
              <AppStack style={{ gap: 10 }}>
                <AppFlexbox
                  style={{
                    gap: 5,
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%'
                  }}
                >
                  <Skeleton height={20} width={100} />
                  <Skeleton height={20} width={50} />
                </AppFlexbox>

                <Skeleton height={10} width="100%" />
                <Skeleton height={10} width="100%" />
                <Skeleton height={10} width="50%" />
              </AppStack>
            </AppCard>
          </AppStack>

          <AppStack style={{ gap: 16, flex: 1 }}>
            <AppCard
              radius={isTabletOrSmaller ? 0 : 'md'}
              shadow="xs"
              style={{ padding: 16 }}
              withBorder
            >
              <AppStack style={{ gap: 10 }}>
                <Skeleton height={10} width="100%" />
                <Skeleton height={10} width="100%" />
                <Skeleton height={10} width="50%" />
              </AppStack>
            </AppCard>
            <AppCard
              radius={isTabletOrSmaller ? 0 : 'md'}
              shadow="xs"
              style={{ padding: 16 }}
              withBorder
            >
              <AppStack style={{ gap: 10 }}>
                <Skeleton height={10} width="100%" />
                <Skeleton height={10} width="100%" />
                <Skeleton height={10} width="50%" />
              </AppStack>
            </AppCard>
          </AppStack>
        </AppFlexbox>
      ) : (
        <AppStack style={{ gap: 16, alignItems: 'center', flex: 1 }}>
          <Skeleton height={10} width="40%" />

          <AppFlexbox style={{ width: '100%', maxWidth: 900, gap: 16 }}>
            <AppCard
              radius={isTabletOrSmaller ? 0 : 'md'}
              shadow="xs"
              style={{ padding: 16, width: '100%' }}
              withBorder
            >
              <AppStack style={{ gap: 10 }}>
                <Skeleton height={10} width="100%" />
                <Skeleton height={10} width="100%" />
                <Skeleton height={10} width="50%" />
              </AppStack>
            </AppCard>
            <AppCard
              radius={isTabletOrSmaller ? 0 : 'md'}
              shadow="xs"
              style={{ padding: 16, width: '100%' }}
              withBorder
            >
              <AppStack style={{ gap: 10 }}>
                <Skeleton height={10} width="100%" />
                <Skeleton height={10} width="100%" />
                <Skeleton height={10} width="50%" />
              </AppStack>
            </AppCard>
          </AppFlexbox>
        </AppStack>
      )}
    </AppStack>
  );
};

PaymentPlanEditView.propTypes = {
  duplicate: PropTypes.bool,
  regAssociation: PropTypes.object
};

export default PaymentPlanEditView;
